import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, flow, makeObservable, observable, runInAction } from "mobx";
import { getSidebarCustomize } from "@devowl-wp/customize";
import { BannerPreset } from "../models/bannerPreset.js";
import { request } from "../utils/request.js";
import { locationRestPresetsBannerGet } from "../wp-api/presetsBanner.get.js";
class CustomizeBannerStore {
    visible = false;
    individualPrivacyOpen = false;
    previewCheckboxActiveState = false;
    previewStickyMenuOpenState = false;
    busyPresets = false;
    presets = new Map();
    rootStore;
    presetConstants = new Map();
    presetDefaults = new Map();
    debounceFromCustomize = {};
    constructor(rootStore){
        makeObservable(this);
        this.rootStore = rootStore;
    }
    fetchPresets = flow(function*() {
        this.busyPresets = true;
        try {
            const { defaults, constants, items } = yield request({
                location: locationRestPresetsBannerGet
            });
            // Save defaults
            for (const did of Object.keys(defaults)){
                this.presetDefaults.set(did, defaults[did]);
            }
            // Save constants
            for (const cid of Object.keys(constants)){
                this.presetConstants.set(cid, constants[cid]);
            }
            // Save presets as object
            for (const pid of Object.keys(items)){
                this.presets.set(pid, new BannerPreset({
                    id: pid,
                    ...items[pid]
                }, this));
            }
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busyPresets = false;
        }
    });
    setBannerFromCustomize(section, setting, value, maybeDebounce = true) {
        const { customizeValuesBanner } = this.rootStore.optionStore.others;
        // Apply debounce for specific settings
        const debounceSetting = setting.toString();
        if (maybeDebounce && [
            "css",
            "animationInDuration",
            "animationOutDuration"
        ].indexOf(debounceSetting) > -1) {
            clearTimeout(this.debounceFromCustomize[debounceSetting]);
            this.debounceFromCustomize[debounceSetting] = setTimeout(()=>this.setBannerFromCustomize(section, setting, value, false), 500);
        } else {
            const currentValue = customizeValuesBanner[section][setting];
            customizeValuesBanner[section][setting] = value;
            // When we want to customize the animation out, let's simulate a "hide"
            if (debounceSetting.startsWith("animationOut") && currentValue !== value) {
                this.forceAnimationOutSimulation();
            }
        }
    }
    setBannerFromPreset(batchUpdates) {
        for (const row of batchUpdates){
            const [section, setting, value] = row;
            this.rootStore.optionStore.others.customizeValuesBanner[section][setting] = value;
        }
    }
    forceAnimationOutSimulation() {
        const { customizeValuesBanner } = this.rootStore.optionStore.others;
        if (customizeValuesBanner["layout"]["animationOut"] === "none") {
            return;
        }
        this.visible = false;
        setTimeout(()=>runInAction(()=>{
                this.visible = true;
            }), +customizeValuesBanner["layout"]["animationOutDuration"] + 1000);
    }
    setVisible(state) {
        this.visible = state;
    }
    setIndividualPrivacyOpen(state) {
        this.individualPrivacyOpen = state;
    }
    setPreviewCheckboxActiveState(state) {
        this.previewCheckboxActiveState = state;
    }
    setPreviewStickyMenuOpenState(state) {
        this.previewStickyMenuOpenState = state;
    }
    /**
     * Allow developers to export the current customize settings via PHP.
     *
     * ```ts
     * realCookieBanner_customize.RootStore.get.customizeBannerStore.exportPhp()
     * ```
     */ exportPhp() {
        const result = {};
        const customize = getSidebarCustomize();
        this.presetDefaults.forEach((defaultValue, key)=>{
            let currentValue = customize(key).get();
            // Fix booleans
            if (typeof defaultValue === "boolean") {
                currentValue = !!+currentValue;
            } else if (!isNaN(currentValue) && currentValue !== "") {
                // Fix integers
                currentValue = +currentValue;
            }
            if (JSON.stringify(defaultValue) !== JSON.stringify(currentValue)) {
                result[this.presetConstants.get(key)] = currentValue;
            }
        });
        // Prepare output
        return this.jsonToPHPArray(result);
    }
    jsonToPHPArray(result) {
        const output = JSON.stringify(result, null, 4).split("\n");
        output.shift();
        output.pop();
        return output.join("\n").replace(/^(\s+)"([A-Za-z\\]+::[A-Z_]+)"(:)/gm, `$1$2 =>`).replace(/^(\s+)([A-Za-z\\]+)::/gm, (full, spaces, namespaces)=>{
            return `${spaces}${namespaces.replace(/\\\\/gm, "\\")}::`;
        });
    }
}
_ts_decorate([
    observable
], CustomizeBannerStore.prototype, "visible", void 0);
_ts_decorate([
    observable
], CustomizeBannerStore.prototype, "individualPrivacyOpen", void 0);
_ts_decorate([
    observable
], CustomizeBannerStore.prototype, "previewCheckboxActiveState", void 0);
_ts_decorate([
    observable
], CustomizeBannerStore.prototype, "previewStickyMenuOpenState", void 0);
_ts_decorate([
    observable
], CustomizeBannerStore.prototype, "busyPresets", void 0);
_ts_decorate([
    observable
], CustomizeBannerStore.prototype, "presets", void 0);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof T === "undefined" ? Object : T,
        typeof P === "undefined" ? Object : P,
        Object,
        void 0
    ]),
    _ts_metadata("design:returntype", void 0)
], CustomizeBannerStore.prototype, "setBannerFromCustomize", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof Array === "undefined" ? Object : Array
    ]),
    _ts_metadata("design:returntype", void 0)
], CustomizeBannerStore.prototype, "setBannerFromPreset", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], CustomizeBannerStore.prototype, "forceAnimationOutSimulation", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], CustomizeBannerStore.prototype, "setVisible", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], CustomizeBannerStore.prototype, "setIndividualPrivacyOpen", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], CustomizeBannerStore.prototype, "setPreviewCheckboxActiveState", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], CustomizeBannerStore.prototype, "setPreviewStickyMenuOpenState", null);
export { CustomizeBannerStore };
